import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import {
  formatBoolean,
  formatCurrency,
  formatIban,
  formatTransactionAmount,
  normalizeIban,
} from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  FinancialProductType,
  SupplementaryPensionSavingsProductFragment,
  useSupplementaryPensionSavingsProductQuery,
  useUpdateSupplementaryPensionSavingsProductMutation,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapSupplementaryPensionSavingsProductToInput } from './utils';

export const SupplementaryPensionSavingsProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useSupplementaryPensionSavingsProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();

  const content: Section<
    SupplementaryPensionSavingsProductFragment,
    Path<SupplementaryPensionSavingsProductFragment>
  >[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:supplementaryPensionSavings.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:supplementaryPensionSavings.detail.name'),
          path: 'name',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:supplementaryPensionSavings.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'contractNumber'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:supplementaryPensionSavings.detail.contractSignDate'),
          path: 'contractSignDate',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'contractSignDate'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:supplementaryPensionSavings.detail.contractEndDate'),
          path: 'contractEndDate',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'contractEndDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:supplementaryPensionSavings.detail.instalment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:supplementaryPensionSavings.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:supplementaryPensionSavings.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentPeriod'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:supplementaryPensionSavings.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:supplementaryPensionSavings.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:supplementaryPensionSavings.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:supplementaryPensionSavings.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:supplementaryPensionSavings.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:supplementaryPensionSavings.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:supplementaryPensionSavings.detail.currentMarketValue'),
          path: 'currentMarketValue.amount',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'currentMarketValue.amount'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:supplementaryPensionSavings.detail.guaranteedFund'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'guaranteedFund',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'guaranteedFund'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:supplementaryPensionSavings.detail.nonGuaranteedFund'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'nonGuaranteedFund',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'nonGuaranteedFund'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:supplementaryPensionSavings.detail.possibleEmployerContribution'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'possibleEmployerContribution',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'possibleEmployerContribution'>,
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:supplementaryPensionSavings.detail.employerContribution'),
          path: 'employerContribution.amount',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'employerContribution.amount'>,
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:supplementaryPensionSavings.detail.employeeContribution'),
          path: 'employeeContribution.amount',
        } as DetailRowType<SupplementaryPensionSavingsProductFragment, 'employeeContribution.amount'>,
      ],
      title: t('products:supplementaryPensionSavings.detail.financialDetails.title', {
        date: format(data?.detail.financialDetailsUpdatedAt ?? new Date(), 'd. M. yyyy'),
      }),
    },
  ];

  const [updateProduct] = useUpdateSupplementaryPensionSavingsProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: SupplementaryPensionSavingsProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapSupplementaryPensionSavingsProductToInput(value) } });
  };

  return (
    <ProductDetail<SupplementaryPensionSavingsProductFragment>
      {...props}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={data?.detail.name ?? t('products:supplementaryPensionSavings.detail.title')}
      type={FinancialProductType.SupplementaryPensionSavings}
    />
  );
};
